import { Component } from '@theme/component';
class DoubleRangeInput extends Component {
  connected = false;

  static get observedAttributes() {
    return ['min', 'max', 'step', 'value-min', 'value-max'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.connected) return;

    this.refreshDOMReferences();
    if (!this.slider || !this.leftInput || !this.rightInput) return;

    this.bindInputEvents();
    this.syncInputConfig();
    this.applyInitialValues();
    this.updateUI();
    this.startObserving();

    this.connected = true;
  }

  reinitialize() {
    this.refreshDOMReferences();
    if (!this.slider || !this.leftInput || !this.rightInput) return;

    this.bindInputEvents();
    this.syncInputConfig();
    this.applyInitialValues();
    this.updateUI();
  }

  disconnectedCallback() {
    if (!this.connected) return;

    this.stopObserving();
    this.unbindInputEvents();
    this.connected = false;
  }

  attributeChangedCallback() {
    if (!this.connected) return;

    this.refreshDOMReferences();
    if (!this.leftInput || !this.rightInput) return;

    this.syncInputConfig();
    this.applyInitialValues();
    this.updateUI();
  }

  refreshDOMReferences() {
    this.slider = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider'));
    this.leftInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.range-slider-input-left'));
    this.rightInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.range-slider-input-right'));

    this.leftTrack = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-val-left'));
    this.rightTrack = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-val-right'));
    this.selectedTrack = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-val-range'));

    this.leftHandle = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-handle-left'));
    this.rightHandle = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-handle-right'));

    this.leftTooltip = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-tooltip-left'));
    this.rightTooltip = /** @type {HTMLElement | null} */ (this.querySelector('.range-slider-tooltip-right'));

    this.leftTooltipText = /** @type {HTMLElement | null} */ (
      this.leftTooltip?.querySelector('.range-slider-tooltip-text') || null
    );
    this.rightTooltipText = /** @type {HTMLElement | null} */ (
      this.rightTooltip?.querySelector('.range-slider-tooltip-text') || null
    );
  }

  bindInputEvents() {
    if (!this.leftInput || !this.rightInput) return;

    this.unbindInputEvents();

    this.onLeftInput = this.handleInput.bind(this, 'left');
    this.onRightInput = this.handleInput.bind(this, 'right');

    this.leftInput.addEventListener('input', this.onLeftInput);
    this.rightInput.addEventListener('input', this.onRightInput);

    this.boundLeftInput = this.leftInput;
    this.boundRightInput = this.rightInput;
  }

  unbindInputEvents() {
    if (this.boundLeftInput && this.onLeftInput) {
      this.boundLeftInput.removeEventListener('input', this.onLeftInput);
    }
    if (this.boundRightInput && this.onRightInput) {
      this.boundRightInput.removeEventListener('input', this.onRightInput);
    }

    this.boundLeftInput = null;
    this.boundRightInput = null;
    this.onLeftInput = null;
    this.onRightInput = null;
  }

  startObserving() {
    if (this.observer) return;

    this.observer = new MutationObserver(() => {
      const nextLeftInput = this.querySelector('.range-slider-input-left');
      const nextRightInput = this.querySelector('.range-slider-input-right');

      if (nextLeftInput !== this.boundLeftInput || nextRightInput !== this.boundRightInput) {
        this.refreshDOMReferences();
        this.bindInputEvents();

        if (this.leftInput && this.rightInput) {
          this.syncInputConfig();
          this.applyInitialValues();
          this.updateUI();
        }
      }
    });

    this.observer.observe(this, { childList: true, subtree: true });
  }

  stopObserving() {
    this.observer?.disconnect();
    this.observer = null;
  }

  get min() {
    return this.parseNumber(this.leftInput?.min, this.parseNumber(this.getAttribute('min'), 0));
  }

  get max() {
    return this.parseNumber(this.leftInput?.max, this.parseNumber(this.getAttribute('max'), 100));
  }

  get step() {
    return this.parseNumber(this.leftInput?.step, this.parseNumber(this.getAttribute('step'), 1));
  }

  get gap() {
    return Math.max(0, Math.min(this.step, this.max - this.min));
  }

  /**
   * @param {string | number | null | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  parseNumber(value, fallback) {
    const parsed = Number.parseFloat(String(value ?? ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * @param {number} value
   * @returns {number}
   */
  clamp(value) {
    return Math.min(this.max, Math.max(this.min, value));
  }

  /**
   * @param {number} value
   * @returns {number}
   */
  percentage(value) {
    const range = this.max - this.min;
    if (range <= 0) return 0;
    return ((value - this.min) / range) * 100;
  }

  syncInputConfig() {
    if (!this.leftInput || !this.rightInput) return;

    const min = this.parseNumber(this.getAttribute('min'), this.parseNumber(this.leftInput?.min, 0));
    const max = this.parseNumber(this.getAttribute('max'), this.parseNumber(this.leftInput?.max, 100));
    const step = this.parseNumber(this.getAttribute('step'), this.parseNumber(this.leftInput?.step, 1));

    this.leftInput.min = String(min);
    this.rightInput.min = String(min);
    this.leftInput.max = String(max);
    this.rightInput.max = String(max);
    this.leftInput.step = String(step);
    this.rightInput.step = String(step);
  }

  applyInitialValues() {
    if (!this.leftInput || !this.rightInput) return;

    const leftValue = this.parseNumber(
      this.getAttribute('value-min'),
      this.parseNumber(this.leftInput.value, this.min),
    );
    const rightValue = this.parseNumber(
      this.getAttribute('value-max'),
      this.parseNumber(this.rightInput.value, this.max),
    );

    this.leftInput.value = String(leftValue);
    this.rightInput.value = String(rightValue);

    this.normalizeValues();
  }

  /**
   * @param {'left' | 'right' | 'initial'} [source]
   */
  normalizeValues(source = 'initial') {
    if (!this.leftInput || !this.rightInput) return;

    let left = this.clamp(this.parseNumber(this.leftInput.value, this.min));
    let right = this.clamp(this.parseNumber(this.rightInput.value, this.max));

    if (left > right) {
      const temp = left;
      left = right;
      right = temp;
    }

    if (this.gap > 0 && right - left < this.gap) {
      if (source === 'left') {
        left = right - this.gap;
      } else {
        right = left + this.gap;
      }

      if (left < this.min) {
        left = this.min;
        right = Math.min(this.max, left + this.gap);
      }

      if (right > this.max) {
        right = this.max;
        left = Math.max(this.min, right - this.gap);
      }
    }

    this.leftInput.value = String(left);
    this.rightInput.value = String(right);
  }

  /**
   * @param {'left' | 'right'} source
   */
  handleInput(source) {
    if (!this.leftInput || !this.rightInput) return;

    this.normalizeValues(source);
    this.updateUI();

    this.dispatchEvent(
      new CustomEvent('double-range-change', {
        bubbles: true,
        composed: true,
        detail: {
          min: this.parseNumber(this.leftInput.value, this.min),
          max: this.parseNumber(this.rightInput.value, this.max),
        },
      }),
    );
  }

  updateUI() {
    if (!this.leftInput || !this.rightInput) return;

    const leftValue = this.parseNumber(this.leftInput.value, this.min);
    const rightValue = this.parseNumber(this.rightInput.value, this.max);

    const leftPct = this.percentage(leftValue);
    const rightPct = this.percentage(rightValue);

    if (this.leftTrack) this.leftTrack.style.width = `${leftPct}%`;
    if (this.rightTrack) this.rightTrack.style.width = `${100 - rightPct}%`;

    if (this.selectedTrack) {
      this.selectedTrack.style.left = `${leftPct}%`;
      this.selectedTrack.style.right = `${100 - rightPct}%`;
    }

    if (this.leftHandle) this.leftHandle.style.left = `${leftPct}%`;
    if (this.rightHandle) this.rightHandle.style.left = `${rightPct}%`;

    if (this.leftTooltip) this.leftTooltip.style.left = `${leftPct}%`;
    if (this.rightTooltip) this.rightTooltip.style.left = `${rightPct}%`;

    if (this.leftTooltipText) this.leftTooltipText.textContent = this.leftInput.value;
    if (this.rightTooltipText) this.rightTooltipText.textContent = this.rightInput.value;
  }
}

if (!customElements.get('double-range-input')) {
  customElements.define('double-range-input', DoubleRangeInput);
}
